import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, Observable, Subscription } from 'rxjs/index';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { BsModalAlertComponent, createAlertModalOptions } from '../core/modal/modal-alert.component';
import { UserAction } from '../user/state/user.action';
import { UserSelectors } from '../user/state/user.selectors';
import { AdaptiveRule } from './state/adaptive-rule.model';
import { AdaptiveRulesService } from './state/adaptive-rules.service';

@Component({
    selector: 'app-adaptive-rules-admin',
    templateUrl: './adaptive-rules-admin.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdaptiveRulesAdminComponent implements OnInit, OnDestroy {
    loading$: Observable<boolean>;
    updatedRule: AdaptiveRule;
    isUpdated = false;
    isNewRule = false;
    selectedUser$: Observable<any>;
    selectedUserAccountSetting$: Observable<any>;
    subscription = new Subscription();
    userRules$: Observable<Array<AdaptiveRule>>;
    canAccessAdaptive = false;
    isAdaptiveRule = false; // Added to disable popup on Search
    viewModel$: Observable<any>;
    selectedRule: AdaptiveRule = undefined;
    deleteLoading = false;

    constructor(private adaptiveRulesService: AdaptiveRulesService,
                private store: Store<any>,
                private userSelectors: UserSelectors,
                private modalService: BsModalService,
                private route: ActivatedRoute) {
        this.selectedUser$ = this.userSelectors.selectSelectedUser();
        this.userRules$ = this.adaptiveRulesService.entities$;
        this.selectedUserAccountSetting$ =  this.userSelectors.selectSelectedUserAccountSettingSelector();
    }

    ngOnInit(): void {
        this.subscription.add(
            this.route.queryParams.subscribe(params => {
                if (params.userid) {
                    this.store.dispatch(UserAction.getSelectedUserById({ userId: Number(params.userid) }));
                }
            })
        );

        this.isAdaptiveRule = true;
        this.adaptiveRulesService.clearCache();
        this.loading$ =  combineLatest([this.adaptiveRulesService.loading$, this.userSelectors.selectUsersSearchLoading()])
            .pipe(
                map(([loading, selectedUserSearchLoading]) =>
                    loading || selectedUserSearchLoading)
            );

        this.selectedUserAccountSetting$
                .pipe(
                    withLatestFrom(this.selectedUser$),
                    filter(([selectedUserAccountSetting, user]) => user !== undefined && user.userId !== 0),
                    filter(([selectedUserAccountSetting]) => selectedUserAccountSetting !== undefined)
                )
            .subscribe(([selectedUserAccountSetting, user]) => {
                    this.canAccessAdaptive = false;
                    let hasAR3Permission = false;
                    this.adaptiveRulesService.clearCache();
                    const index = user.userPermissions.findIndex((permission: any) => permission.permissionKey === 'autoremind3');
                    if (index !== -1) {
                        hasAR3Permission = true;
                    }
                    if (hasAR3Permission) {
                        this.canAccessAdaptive = true;
                        this.adaptiveRulesService.getWithQuery({userId: user.userId});
                    } else {
                        if (this.isAdaptiveRule) {
                            this.modalService
                                .show(BsModalAlertComponent,
                                    createAlertModalOptions('Alert', 'The User has no access to create Adaptive Rules - either AR3 User or have Central Scheduler as beta'));
                        }
                    }
                }
            );

        this.viewModel$ = combineLatest([
            this.loading$,
            this.userRules$,
            this.selectedUser$]
        )
            .pipe(
                map(([loading, userRules, selectedUser]) =>
                    ({
                        loading, userRules, selectedUser
                    }))
            );
    }

    saveRule(selectedUserId): void {
        this.isUpdated = false;

        this.updatedRule.userId = selectedUserId;
        if (this.isNewRule) {
            if (this.updatedRule.mediaType === 'voice' && this.updatedRule.ruleName.indexOf('voice') === -1) {
                const ruleName = this.updatedRule.ruleName.startsWith('voice_')
                   ? this.updatedRule.ruleName
                   : `voice_${this.updatedRule.ruleName}`;
                this.updatedRule.ruleName = ruleName;
            }
            if (this.updatedRule.mediaType === 'twiliovoice') {
                const ruleName = this.updatedRule.ruleName.startsWith('twiliovoice_')
                   ? this.updatedRule.ruleName
                   : `twiliovoice_${this.updatedRule.ruleName}`;
                this.updatedRule.ruleName = ruleName;
            }

            this.subscription.add(this.adaptiveRulesService.add(this.updatedRule).subscribe(_ => {
                this.adaptiveRulesService.clearCache();
                this.adaptiveRulesService.getWithQuery({userId: selectedUserId});
            }));
        } else {
            this.subscription.add(this.adaptiveRulesService.update(this.updatedRule).subscribe(_ => {
                this.adaptiveRulesService.clearCache();
                this.adaptiveRulesService.getWithQuery({userId: selectedUserId});
            }));
        }
    }

    updateRule(event: AdaptiveRule, userRules): void {
        this.isUpdated = false;
        if (event !== undefined && event.type === 'map') {
            this.updatedRule = event;
            if (this.isNewRule) {
                this.isUpdated = this.updatedRule.ruleName !== undefined &&
                    this.updatedRule.ruleName !== null && this.updatedRule.ruleName !== '';
            }
            userRules.map(userRule => {
                if (!this.isNewRule) {
                    if (this.updatedRule.ruleName === userRule.ruleName) {
                        let isMapUpdated = false; // if value in map array is updated
                        this.updatedRule.map.findIndex(rule => {
                            const key = Object.keys(rule)[0];
                            const value = Object.values(rule)[0];
                            userRule.map.find(userrule => {
                                const userrulekey = Object.keys(userrule)[0];
                                const userrulevalue = Object.values(userrule)[0];
                                if (userrulekey === key) {
                                    isMapUpdated = userrulevalue !== value;
                                }
                            });
                        });
                        this.isUpdated = this.updatedRule.mediaType !== userRule.mediaType ||
                            this.updatedRule.default !== userRule.default || isMapUpdated ||
                            this.updatedRule.map.length !== userRule.map.length;
                    }
                }

                return userRule;
            });
        }
    }

    isNew(event): void {
        this.isNewRule = event;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.isAdaptiveRule = false;
    }

    selectRule(event: AdaptiveRule, selectedUserId): void {
        this.deleteLoading = false;
        if (event === undefined) {
            this.selectedRule = event;
        } else if (event.type === 'map') {
            const selectedRule = {...event};
            selectedRule.userId = selectedUserId;
            this.selectedRule = selectedRule;
        }
    }

    deleteRule(selectedUserId): void {
        this.deleteLoading = true;
        if (!this.isNewRule) {
            this.subscription.add(
                this.adaptiveRulesService.canDeleteRule(selectedUserId, this.selectedRule.ruleName).subscribe(response => {
                if (response.canDelete) {
                    this.deleteSelectedRule(selectedUserId);
                } else {
                    const templateNames = response.templateNames;
                    this.deleteLoading = false;
                    this.modalService
                        .show(BsModalAlertComponent, createAlertModalOptions('Alert',
                            `The Adaptive rule is being used in ${templateNames}. Please remove it first?`));
                }
            }));
        }
    }

    deleteSelectedRule(selectedUserId): void {
        this.subscription.add(this.adaptiveRulesService.deleteRule(selectedUserId, this.selectedRule.ruleName)
           .subscribe(_ => {
                this.adaptiveRulesService.clearCache();
                this.adaptiveRulesService.getWithQuery({userId: selectedUserId});
                this.isUpdated = false;
            })
        );
    }
}
